import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingDown, Route, Loader2 } from 'lucide-react';

/**
 * FleetSavingsCard — fleet-wide fuel-savings stat for the Admin Dispatch
 * dashboard.
 *
 * Data source: `GET /api/admin/route-optimization/stats` aggregates
 * over the `route_optimization_log` collection, which receives a row
 * every time a driver taps "Navigate all stops" in the multi-stop
 * optimizer. We show:
 *   - Big number: km saved last 7 days (the "you saved this week" metric)
 *   - Lifetime savings + run count (cumulative impact)
 *   - Top 3 driver leaderboard for the week (encourages adoption)
 *
 * Why km-saved (vs say fuel-cost): fuel price varies, vehicle MPG
 * varies, but km saved is a cleanly computed ratio against the naive
 * received-order baseline. Admins can multiply by their own fuel
 * cost if they want a riyal figure — we don't guess.
 *
 * Refresh cadence: polls every 60s. The optimizer only fires when a
 * driver taps Navigate, so the log doesn't grow fast — no need for
 * aggressive polling.
 */
const FleetSavingsCard = ({ apiBase, authHeaders }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await axios.get(
          `${apiBase}/admin/route-optimization/stats`,
          { headers: authHeaders },
        );
        if (alive) setStats(data);
      } catch {
        if (alive) setStats(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [apiBase, authHeaders]);

  if (loading && !stats) {
    return (
      <div className="bg-white rounded-2xl border border-neutral-200 p-5 flex items-center gap-2 text-sm text-neutral-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading fleet savings…
      </div>
    );
  }

  // If there are no runs yet, render an explainer card (so the admin
  // knows the feature exists and what unlocks it).
  const lifetime = stats?.lifetime || { total_runs: 0, saved_km: 0 };
  const week = stats?.last_week || { total_runs: 0, saved_km: 0, total_stops: 0 };
  const topDrivers = stats?.top_drivers_week || [];

  if (lifetime.total_runs === 0) {
    return (
      <div
        className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl border border-emerald-200 p-5"
        data-testid="fleet-savings-card"
      >
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center">
            <Route className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-[0.2em] text-emerald-700 uppercase">
              Multi-stop optimizer
            </div>
            <h2 className="font-display text-lg font-bold text-[#0F172A] leading-tight">
              Fuel savings tracker
            </h2>
          </div>
        </div>
        <p className="text-xs text-neutral-600 leading-snug">
          Once drivers start tapping <b>Navigate all stops</b> in their dashboard,
          this card will track how many km they save per week vs the order
          they received the deliveries.
        </p>
      </div>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-2xl p-5 shadow-xl shadow-emerald-900/20"
      data-testid="fleet-savings-card"
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
          <TrendingDown className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] text-emerald-100 uppercase">
            Multi-stop · 7 days
          </div>
          <h2 className="font-display text-lg font-bold leading-tight">
            Fleet fuel savings
          </h2>
        </div>
      </div>

      <div className="mb-3">
        <div
          className="text-4xl font-display font-bold leading-none"
          data-testid="fleet-savings-week-km"
        >
          {week.saved_km.toFixed(1)}
          <span className="text-xl ml-1.5 text-emerald-100">km</span>
        </div>
        <div className="text-xs text-emerald-100 mt-1">
          saved across {week.total_runs} route{week.total_runs === 1 ? '' : 's'} ·{' '}
          {week.total_stops} stops
        </div>
      </div>

      {/* Lifetime + leaderboard */}
      <div className="border-t border-white/15 pt-3 grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[10px] font-bold tracking-wider text-emerald-200 uppercase">
            Lifetime saved
          </div>
          <div className="text-base font-bold" data-testid="fleet-savings-lifetime-km">
            {lifetime.saved_km.toFixed(1)} km
          </div>
        </div>
        <div>
          <div className="text-[10px] font-bold tracking-wider text-emerald-200 uppercase">
            Lifetime runs
          </div>
          <div className="text-base font-bold">{lifetime.total_runs}</div>
        </div>
      </div>

      {topDrivers.length > 0 && (
        <div className="border-t border-white/15 pt-3">
          <div className="text-[10px] font-bold tracking-wider text-emerald-200 uppercase mb-1.5">
            Top drivers · 7 days
          </div>
          <ol className="space-y-1" data-testid="fleet-savings-top-drivers">
            {topDrivers.slice(0, 3).map((d, i) => (
              <li key={d.driver_id} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="w-4 h-4 rounded-full bg-white/15 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="truncate">{d.driver_name}</span>
                </span>
                <span className="text-emerald-100 font-semibold flex-shrink-0 ml-2">
                  {d.saved_km.toFixed(1)} km
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default FleetSavingsCard;
